import { AllCardsType } from '../../../data/all-cards'
import { NombreTarotista } from '../../../data/personality'
import { DataTypePregunta } from '../../../interfaces/utils'
import { buildComboPreamble, ComboPreviousStep } from '../../../utils/combo-context'
import { formatCardLabel } from '../../../utils/card-label'
import {
    RENACER_ALMA_NAME,
    RENACER_ALMA_PREAMBLE,
    RENACER_ALMA_STEPS,
    RENACER_ALMA_TOTAL_STEPS,
} from './combo-config'

const cardLabel = (card: AllCardsType) => formatCardLabel(card)

const consultaExtra = (data: DataTypePregunta) =>
    data.consulta.length > 0
        ? 'ten en cuenta en la tirada la siguiente consulta que realiza el consultante: ' +
          data.consulta +
          ','
        : ''

const buildStepPrompt = (step: number, cards: AllCardsType[], data: DataTypePregunta): string => {
    const extra = consultaExtra(data)

    if (step === 1) {
        return `Quiero que interpretes una **Tirada de Temporada Actual del Alma** con 10 cartas, ayudando al consultante ${
            data.consultante
        } a comprender qué estación o ciclo del alma está transitando en este momento de su vida, ${extra} según estas cartas: **Energía de la temporada actual (¿qué estación o energía del alma está transitando ahora?):** ${cardLabel(
            cards[0]
        )}, **Intensidad o duración aproximada (¿qué tan fuerte o cuánto tiempo puede durar esta temporada?):** ${cardLabel(
            cards[1]
        )}, **Propósito principal (¿para qué está esta temporada? ¿Qué quiere trabajar mi alma?):** ${cardLabel(
            cards[2]
        )}, **Lección secundaria / sombra (¿qué otra lección o aspecto sombra está presente?):** ${cardLabel(
            cards[3]
        )}, **Qué necesita ser soltado (¿qué está invitado/a a soltar en este ciclo?):** ${cardLabel(
            cards[4]
        )}, **Qué necesita ser cultivado (¿qué está invitado/a a nutrir o abrirse a recibir?):** ${cardLabel(
            cards[5]
        )}, **Influencias externas (¿qué factores externos están afectando esta temporada?):** ${cardLabel(
            cards[6]
        )}, **Resistencia o miedo (¿qué parte del consultante se está resistiendo o teniendo miedo?):** ${cardLabel(
            cards[7]
        )}, **El regalo/bendición que trae (¿cuál es el regalo oculto o la bendición de esta temporada?):** ${cardLabel(
            cards[8]
        )} y **Consejo del alma + transición (¿cómo transitarla con gracia? ¿qué temporada se asoma después?):** ${cardLabel(
            cards[9]
        )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente sobre el ciclo actual del alma: conecta la energía de la temporada, su propósito, las lecciones, lo que debe soltarse y cultivarse, y la transición hacia lo que viene. Detecta patrones, resistencias y bendiciones ocultas. Proporciona consejos prácticos y espirituales para transitar esta temporada con conciencia y gracia. Mantén un tono profesional, empático, profundo y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
    }

    if (step === 2) {
        return `Quiero que interpretes una **Tirada de Sanación del Niño Interior** con 10 cartas, ayudando al consultante ${
            data.consultante
        } a conectar con su niño interior, comprender sus heridas y abrir un camino de sanación y reparenting, ${extra} según estas cartas: **Estado actual de tu niño interior (¿cómo está tu niño interior hoy?):** ${cardLabel(
            cards[0]
        )}, **La herida principal que carga (¿cuál es la herida más fuerte que sigue presente?):** ${cardLabel(
            cards[1]
        )}, **Cómo se manifiesta en tu vida adulta (¿de qué forma aparece esta herida hoy?):** ${cardLabel(
            cards[2]
        )}, **Qué necesitó y no recibió (¿qué necesitaba ese niño y no obtuvo?):** ${cardLabel(
            cards[3]
        )}, **Creencia que se formó (¿qué creencia limitante se generó a partir de esa herida?):** ${cardLabel(
            cards[4]
        )}, **Cómo te limita o sabotea hoy (¿de qué forma te está afectando actualmente?):** ${cardLabel(
            cards[5]
        )}, **Qué necesita tu niño interior ahora (¿qué necesita recibir de vos en este momento?):** ${cardLabel(
            cards[6]
        )}, **Cómo podés reparentarlo (¿qué acción concreta podés hacer para sanarlo?):** ${cardLabel(
            cards[7]
        )}, **El regalo que trae la sanación (¿qué fuerza o don te va a dar sanar esta herida?):** ${cardLabel(
            cards[8]
        )} y **Mensaje final de tu niño interior (¿qué quiere decirte tu niño interior ahora?):** ${cardLabel(
            cards[9]
        )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Teje una lectura coherente sobre la herida del niño interior: conecta el pasado con el presente, la creencia limitante, las necesidades no cubiertas y el camino de sanación. Detecta patrones de autosabotaje, bloqueos emocionales y oportunidades de reparenting. Proporciona consejos prácticos, tiernos y concretos para sanar y cuidar al niño interior. Mantén un tono profesional, empático, contenido y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
    }

    return `Quiero que interpretes una **Tirada de Conexión con tu Yo Futuro** con 10 cartas, ayudando al consultante ${
        data.consultante
    } a conectar con la versión de sí mismo que puede llegar a ser en los próximos 1 a 3 años, ${extra} según estas cartas: **Energía actual del consultante:** ${cardLabel(
        cards[0]
    )}, **Lo que más está pidiendo atención ahora:** ${cardLabel(cards[1])}, **Energía y presencia del Yo Futuro (quién se convierte esta persona dentro de 1 a 3 años; enfócate en su identidad, energía, presencia y cualidades principales):** ${cardLabel(
        cards[2]
    )}, **Cómo se siente emocionalmente esa versión futura:** ${cardLabel(cards[3])}, **Qué está creando o viviendo esa versión futura:** ${cardLabel(
        cards[4]
    )}, **Qué necesita soltar para llegar ahí:** ${cardLabel(cards[5])}, **Qué necesita cultivar o activar:** ${cardLabel(
        cards[6]
    )}, **Apoyo invisible o guías que ya tiene disponible:** ${cardLabel(cards[7])}, **El paso más poderoso y realista que puede dar ahora:** ${cardLabel(
        cards[8]
    )} y **Mensaje final de su Yo Futuro + visión del camino si sigue esta línea:** ${cardLabel(
        cards[9]
    )}. Explica detalladamente cada carta y su significado en la posición donde ha salido. Conecta el presente con el yo futuro: muestra cómo la energía actual, lo que pide atención y lo que debe soltar o cultivar se relacionan con la identidad y el camino que revela el yo futuro. Detecta patrones, bloqueos y oportunidades en la lectura. Proporciona consejos prácticos, realistas y motivadores para acercarse a esa versión futura. Mantén un tono profesional, empático, esperanzador y comprensible. Dirígete directamente al consultante, no utilices la tercera persona refiriéndote al consultante, aún cuando las consultas se encuentren en tercera persona, adapta la respuesta para que quede en segunda persona. Incluye además en 2 párrafos después de la lectura, una respuesta directa para el consultante y un consejo final (extra-tarot).`
}

export const interpretStep = (
    step: number,
    cards: AllCardsType[],
    data: DataTypePregunta,
    user: string,
    personality: NombreTarotista,
    previousSteps: ComboPreviousStep[],
    comboId: string
) => {
    const config = RENACER_ALMA_STEPS[step - 1]
    const preamble = buildComboPreamble(
        RENACER_ALMA_NAME,
        step,
        RENACER_ALMA_TOTAL_STEPS,
        previousSteps,
        RENACER_ALMA_PREAMBLE
    )
    const consult = preamble + buildStepPrompt(step, cards, data)

    return {
        consult,
        user,
        personality,
        clientName: data.consultante.name,
        clientPhone: data.consultante.phone || '',
        clientAnswer: data.consulta,
        cards,
        type: config.type,
        comboId,
        comboName: RENACER_ALMA_NAME,
        comboStep: step,
        comboTotalSteps: RENACER_ALMA_TOTAL_STEPS,
    }
}
